import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react';

const LAST_ONLINE_KEY = 'zbk3_last_online_at';

interface OnlineStatus {
  isOnline: boolean;
  lastOnlineAt: Date | null;
}

function getSnapshot(): boolean {
  return navigator.onLine;
}

function getServerSnapshot(): boolean {
  return true; // SSR always assumes online
}

function subscribe(callback: () => void): () => void {
  window.addEventListener('online', callback);
  window.addEventListener('offline', callback);
  return () => {
    window.removeEventListener('online', callback);
    window.removeEventListener('offline', callback);
  };
}

/**
 * Tracks browser online/offline status reactively.
 * Uses useSyncExternalStore for tear-free reads.
 *
 * @returns { isOnline, lastOnlineAt }
 */
/**
 * Restore persisted lastOnlineAt from localStorage (survives app restart).
 */
function getPersistedLastOnlineAt(): Date | null {
  try {
    const raw = localStorage.getItem(LAST_ONLINE_KEY);
    if (!raw) return null;
    const date = new Date(raw);
    return Number.isNaN(date.getTime()) ? null : date;
  } catch {
    return null;
  }
}

function persistLastOnlineAt(date: Date): void {
  try {
    localStorage.setItem(LAST_ONLINE_KEY, date.toISOString());
  } catch {
    // Ignore — localStorage may be unavailable
  }
}

/**
 * Tracks browser online/offline status reactively.
 * Uses useSyncExternalStore for tear-free reads.
 * Persists lastOnlineAt to localStorage so it survives app restarts.
 *
 * @returns { isOnline, lastOnlineAt }
 */
export function useOnlineStatus(): OnlineStatus {
  const isOnline = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
  const lastOnlineAtRef = useRef<Date | null>(
    isOnline ? new Date() : getPersistedLastOnlineAt(),
  );
  const [lastOnlineAt, setLastOnlineAt] = useState<Date | null>(lastOnlineAtRef.current);

  useEffect(() => {
    if (isOnline) {
      const now = new Date();
      lastOnlineAtRef.current = now;
      setLastOnlineAt(now);
      persistLastOnlineAt(now);
    }
  }, [isOnline]);

  return { isOnline, lastOnlineAt };
}
